import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  PlusIcon,
  FunnelIcon,
  UsersIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { segmentsAPI, Segment } from '../../services/api';
import CreateSegmentModal from './CreateSegmentModal';
import { Card, CardBody, Button, Badge, Spinner, EmptyState, Modal, PageHero } from '../ui';

const SegmentsList: React.FC = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const basePath = isAdminRoute ? '/admin/segments' : '/segments';

  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState<Segment | null>(null);

  useEffect(() => {
    loadSegments();
  }, []);

  const loadSegments = async () => {
    try {
      setLoading(true);
      const data = await segmentsAPI.getAll();
      setSegments(data);
    } catch (error) {
      console.error('Failed to load segments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (segment: Segment) => {
    setSegmentToDelete(segment);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!segmentToDelete) return;

    try {
      await segmentsAPI.delete(segmentToDelete.id);
      setSegments(segments.filter(s => s.id !== segmentToDelete.id));
      setShowDeleteModal(false);
      setSegmentToDelete(null);
    } catch (error) {
      console.error('Failed to delete segment:', error);
    }
  };

  const handleEdit = (segment: Segment) => {
    setSelectedSegment(segment);
    setShowCreateModal(true);
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setSelectedSegment(null);
    loadSegments();
  };

  const getSegmentColor = (color?: string) => {
    return color || '#6366f1'; // Default to primary color
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Enhanced Hero Section */}
      <PageHero
        title="Client Segments"
        subtitle="Organize and target your clients with dynamic and static segments"
        icon={<FunnelIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="purple"
      >
        <div className="mt-2">
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            leftIcon={<PlusIcon className="h-5 w-5" />}
          >
            Create Segment
          </Button>
        </div>
      </PageHero>

      {/* Segments Grid */}
      {segments.length === 0 ? (
        <EmptyState
          icon={<FunnelIcon className="h-12 w-12" />}
          title="No segments"
          description="Get started by creating a new segment."
          action={
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              Create Segment
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {segments.map((segment) => (
            <Card key={segment.id} variant="elevated" hoverable className="overflow-hidden">
              {/* Color Bar */}
              <div
                className="h-2"
                style={{ backgroundColor: getSegmentColor(segment.color) }}
              ></div>

              <CardBody className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-neutral-900 mb-1">
                      {segment.name}
                    </h3>
                    {segment.description && (
                      <p className="text-sm text-neutral-500 line-clamp-2">
                        {segment.description}
                      </p>
                    )}
                  </div>
                  <Badge
                    variant={segment.type === 'DYNAMIC' ? 'primary' : 'secondary'}
                    size="sm"
                    className="ml-2"
                  >
                    {segment.type}
                  </Badge>
                </div>

                {/* Stats */}
                <div className="flex items-center text-sm text-neutral-600 mb-4">
                  <UsersIcon className="h-5 w-5 mr-2 text-neutral-400" />
                  <span className="font-medium">
                    {segment._count?.clients || 0} clients
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-4 border-t border-neutral-200">
                  <Link
                    to={`${basePath}/${segment.id}`}
                    className="flex-1"
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<ChartBarIcon className="h-4 w-4" />}
                      className="w-full"
                    >
                      View
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(segment)}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(segment)}
                    className="text-error-600 border-error-300 hover:bg-error-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CreateSegmentModal
          isOpen={showCreateModal}
          onClose={handleModalClose}
          segment={selectedSegment}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal && !!segmentToDelete}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Segment"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Are you sure you want to delete "{segmentToDelete?.name}"? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SegmentsList;

